<script setup>
/**
 * A step that DOES something in the owner's own system — the booking that gets made, the
 * order that gets placed.
 *
 * ── WHY THIS ONE HAS A NAME BOX AND THE QUESTION SCREEN DOES NOT ──────────────────────
 * Every other step's name is generated from the question it asks. An action asks nothing —
 * `CallStep` has no `instruction` and no question (`types.ts:147`) — so there is no sentence
 * to generate one from, and `name` is `.min(1)` in the schema. One short box, labelled with
 * what the owner would say out loud ("Check the calendar"), is the whole identity of this
 * step: it is what the map draws, what the trace prints, and what the What-if screen calls
 * this step when it asks what happens if it breaks.
 *
 * ── AND WHY "IF IT FAILED" IS A SENTENCE HERE RATHER THAN A PICKER ────────────────────
 * `decide.ts` reads no `onFailed` as "the owner never said what to do when this breaks" and
 * hands the chat to a person. That is the answer almost every owner wants, so it is the
 * default and it is printed. Changing it is one row, opening in place, on the What-if
 * screen where every other failure arm is answered together.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import WasloInput from '../WasloInput.vue';
import WasloSelect from '../WasloSelect.vue';
import { useWasloI18n } from '../../i18n.js';
import { toIntlLocale } from '../../helpers/intl.js';
import { MAX_NAME } from '../../helpers/task-flow-draft.js';
import { purposeOf } from '../../helpers/connection-draft.js';

const props = defineProps({
  /** A spine row whose step is a `call`. */
  row: { type: Object, required: true },
  /** `[{name, description}]` or `null` when the list could not be read. */
  connections: { type: Array, default: null },
});

const emit = defineEmits(['update:row', 'wiring']);

const { t, locale } = useWasloI18n();

const key = name => `WASLO.TASKS.COMPOSE.ACTION.${name}`;

const format = computed(
  () => new Intl.NumberFormat(toIntlLocale(locale.value))
);

const step = computed(() => props.row.step ?? {});

const patchStep = changes =>
  emit('update:row', { ...props.row, step: { ...step.value, ...changes } });

const connectionChoices = computed(() =>
  (props.connections ?? []).map(row => ({
    value: row.name,
    label: row.description ? `${row.name}: ${row.description}` : row.name,
  }))
);

const hasConnection = computed(() => (step.value.connection ?? '') !== '');

/** P-3.4: a step that changes her system runs only after a yes. Said where she picks it. */
const changesSomething = computed(
  () =>
    purposeOf(
      (props.connections ?? []).find(row => row.name === step.value.connection)
    ) === 'change'
);

const sendsNote = computed(() => {
  const inputs = Object.entries(step.value.inputs ?? {});
  if (inputs.length === 0) return t(key('SENDS_NONE'));
  return t(key('SENDS'), { count: format.value.format(inputs.length) });
});

const keepsNote = computed(() => {
  const keep = Object.entries(step.value.keep ?? {});
  if (keep.length === 0) return t(key('KEEPS_NOTHING'));
  return t(key('KEEPS'), { count: format.value.format(keep.length) });
});

const failsNote = computed(() =>
  props.row.ifFailed?.go === 'step'
    ? t(key('FAILS_STEP'))
    : t(key('FAILS_HANDOVER'))
);
</script>

<template>
  <div class="flex flex-col gap-6" data-waslo-action-step>
    <WasloInput
      :model-value="step.name ?? ''"
      :label="t(key('NAME'))"
      :hint="t(key('NAME_HINT'))"
      :placeholder="t(key('NAME_PLACEHOLDER'))"
      :message="
        (step.name ?? '').length > MAX_NAME
          ? t(key('NAME_LONG'), { limit: format.format(MAX_NAME) })
          : ''
      "
      message-type="error"
      data-waslo-action-name
      @update:model-value="value => patchStep({ name: value })"
    />

    <div class="flex flex-col gap-3">
      <WasloSelect
        :model-value="step.connection ?? ''"
        :label="t(key('SYSTEM'))"
        :options="connectionChoices"
        :placeholder="t(key('SYSTEM_PLACEHOLDER'))"
        :message="connections === null ? t(key('SYSTEM_UNAVAILABLE')) : ''"
        message-type="error"
        data-waslo-action-system
        @update:model-value="value => patchStep({ connection: value })"
      />
      <p
        v-if="(connections ?? []).length === 0 && connections !== null"
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-action-no-systems
      >
        {{ t(key('SYSTEM_NONE')) }}
      </p>
      <p
        v-if="changesSomething"
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-action-change-note
      >
        {{ t(key('CHANGE_NOTE')) }}
      </p>

      <div
        v-if="hasConnection"
        class="flex flex-col gap-3 p-4 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
        data-waslo-action-wiring-card
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t(key('WIRING')) }}
          </p>
          <Button
            :label="t(key('WIRING_OPEN'))"
            icon="i-lucide-settings-2"
            variant="faded"
            color="slate"
            size="sm"
            data-waslo-action-wiring-open
            @click="emit('wiring')"
          />
        </div>
        <p class="mb-0 text-sm text-n-slate-11">{{ sendsNote }}</p>
        <p class="mb-0 text-sm text-n-slate-11">{{ keepsNote }}</p>
      </div>
    </div>

    <p class="mb-0 text-sm text-n-slate-11" data-waslo-action-fails>
      {{ failsNote }}
    </p>
  </div>
</template>
